import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

export type PollOption = {
  id: string;
  text: string | null;
  image_url: string | null;
  votes_count: number;
  has_voted: boolean;
};

export type Poll = {
  id: string;
  title: string;
  description: string | null;
  type: 'question' | 'cover_comparison';
  created_at: string;
  ends_at: string;
  closed: boolean;
  options: PollOption[];
  total_votes: number;
};

export function usePolls(ideaId: string) {
  const [polls, setPolls] = useState<Poll[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    fetchPolls();
  }, [ideaId, user]);

  const fetchPolls = async () => {
    try {
      // First get all polls for the idea
      const { data: pollsData, error: pollsError } = await supabase
        .from('polls')
        .select(`
          *,
          options:poll_options(*)
        `)
        .eq('idea_id', ideaId)
        .order('created_at', { ascending: false });

      if (pollsError) throw pollsError;

      // Get all poll IDs
      const pollIds = pollsData?.map(p => p.id) || [];

      // Get user's votes if logged in
      let userVotes: Record<string, string> = {};
      if (user && pollIds.length > 0) {
        const { data: votesData } = await supabase
          .from('poll_votes')
          .select('poll_id, option_id')
          .eq('user_id', user.id)
          .in('poll_id', pollIds);

        userVotes = (votesData || []).reduce((acc, vote) => ({
          ...acc,
          [vote.poll_id]: vote.option_id
        }), {});
      }

      // Get vote counts for each option
      const optionIds = pollsData?.flatMap(p => p.options.map(o => o.id)) || [];
      const { data: voteCounts } = await supabase
        .from('poll_votes')
        .select('option_id')
        .in('option_id', optionIds);

      // Count votes for each option
      const optionVoteCounts = (voteCounts || []).reduce((acc, vote) => {
        acc[vote.option_id] = (acc[vote.option_id] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      // Process the data
      const processedPolls = pollsData?.map(poll => {
        const processedOptions = poll.options.map(option => ({
          ...option,
          votes_count: optionVoteCounts[option.id] || 0,
          has_voted: option.id === userVotes[poll.id]
        }));

        const totalVotes = processedOptions.reduce((sum, opt) => sum + opt.votes_count, 0);

        return {
          ...poll,
          options: processedOptions,
          total_votes: totalVotes
        };
      }) || [];

      setPolls(processedPolls);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch polls');
      setPolls([]);
    } finally {
      setLoading(false);
    }
  };

  const createPoll = async (
    title: string,
    type: 'question' | 'cover_comparison',
    options: { text?: string; image_url?: string }[],
    description?: string,
    endsAt?: Date
  ) => {
    if (!user) return;

    try {
      const { data: poll, error: pollError } = await supabase
        .from('polls')
        .insert({
          idea_id: ideaId,
          title,
          description,
          type,
          ends_at: endsAt || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
        })
        .select()
        .single();

      if (pollError) throw pollError;

      const { error: optionsError } = await supabase
        .from('poll_options')
        .insert(
          options.map(opt => ({
            poll_id: poll.id,
            text: opt.text,
            image_url: opt.image_url
          }))
        );

      if (optionsError) throw optionsError;

      await fetchPolls();
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to create poll');
    }
  };

  const vote = async (pollId: string, optionId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('poll_votes')
        .insert({
          poll_id: pollId,
          option_id: optionId,
          user_id: user.id
        });

      if (error) throw error;

      await fetchPolls();
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to vote');
    }
  };

  const closePoll = async (pollId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('polls')
        .update({ closed: true })
        .eq('id', pollId);

      if (error) throw error;

      await fetchPolls();
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to close poll');
    }
  };

  const deletePoll = async (pollId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('polls')
        .delete()
        .eq('id', pollId);

      if (error) throw error;

      await fetchPolls();
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to delete poll');
    }
  };

  return {
    polls,
    loading,
    error,
    createPoll,
    vote,
    closePoll,
    deletePoll
  };
}